// watchedPackages.js
// ──────────────────────────────────────────────────────────────────────────
// Tracks which packages a user has fully watched (i.e. completed the entire
// 5-step link-bypass flow).
//
// Two backing stores:
//   1. localStorage  →  used for ALL users (anonymous + logged-in). Acts as
//      the offline cache and the canonical source for guests.
//   2. Firebase Realtime Database (profiles/{uid}/watchedPackages) → used
//      ONLY for logged-in (non-anonymous) users. Lets the "watched" mark
//      follow them across browsers / devices / sessions.
//
// All UI components subscribe via the global "packagesUpdated" event so a
// mark in one tab instantly reflects everywhere.
// ──────────────────────────────────────────────────────────────────────────

import { ref, get, update } from "firebase/database";
import { db, auth } from "../firebase";

const LS_KEY = "watchedPackages";
const FIRE_PATH = (uid) => `profiles/${uid}/watchedPackages`;

// ── Internal helpers ─────────────────────────────────────────────────────
const readLocal = () => {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || "{}") || {};
  } catch {
    return {};
  }
};

const writeLocal = (obj) => {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(obj || {}));
  } catch {}
};

const broadcast = () => {
  try {
    window.dispatchEvent(new Event("packagesUpdated"));
  } catch {}
};

// ── Public: is the given package id marked as watched? ──────────────────
export const isPackageWatched = (performerId) => {
  if (performerId == null) return false;
  const watched = readLocal();
  return !!watched[String(performerId)];
};

// ── Public: mark a package as watched (writes to BOTH stores) ───────────
// Safe to call repeatedly; idempotent.
export const markPackageWatched = (performerId) => {
  if (!performerId) return;
  const key = String(performerId);
  const watched = readLocal();
  if (!watched[key]) {
    watched[key] = Date.now();
    writeLocal(watched);
    broadcast();
  }

  // For logged-in users, mirror the entry into Firebase so it follows them.
  // We deliberately fire-and-forget — UI never has to wait on the network.
  try {
    const user = auth.currentUser;
    if (user && !user.isAnonymous) {
      const updates = {};
      updates[`${FIRE_PATH(user.uid)}/${key}`] = Date.now();
      update(ref(db), updates).catch(() => {});
    }
  } catch {}
};

// ── Public: pull the user's remote "watchedPackages" map and merge it
// into local storage. Call this right after a successful login (and once
// at app startup when a user is detected). Guests are no-ops. ──────────
export const syncWatchedFromFirebase = async () => {
  try {
    const user = auth.currentUser;
    if (!user || user.isAnonymous) return;
    const snap = await get(ref(db, FIRE_PATH(user.uid)));
    if (!snap.exists()) return;
    const remote = snap.val() || {};
    if (typeof remote !== "object") return;

    const local = readLocal();
    let changed = false;
    Object.keys(remote).forEach((k) => {
      if (!local[k]) {
        local[k] = remote[k] || Date.now();
        changed = true;
      }
    });

    // Push any local-only keys back to Firebase so the union is preserved.
    const localOnly = Object.keys(local).filter((k) => !remote[k]);
    if (localOnly.length) {
      const updates = {};
      localOnly.forEach((k) => {
        updates[`${FIRE_PATH(user.uid)}/${k}`] = local[k] || Date.now();
      });
      update(ref(db), updates).catch(() => {});
    }

    if (changed) {
      writeLocal(local);
      broadcast();
    }
  } catch {
    // Silent fail — local cache continues to work.
  }
};
